// pages/Cases.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import {
  AlertCircle,
  Plus,
  Search,
  User,
  Building2,
  Clock,
  Calendar,
  LayoutList,
  LayoutGrid,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Layers,
  Users,
} from "lucide-react";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import CreateCaseModal from "../../components/CreateCaseModal";
import AutoAssignCasesModal from "../../components/AutoAssignCasesModal";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

// Detect if touch device
const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const ItemTypes = {
  CASE: "case",
};

const statusConfig = {
  New: { label: "New", color: "bg-blue-600", textColor: "text-white" },
  "In Progress": {
    label: "In Progress",
    color: "bg-yellow-500",
    textColor: "text-white",
  },
  "On Hold": {
    label: "On Hold",
    color: "bg-orange-500",
    textColor: "text-white",
  },
  Escalated: {
    label: "Escalated",
    color: "bg-red-600",
    textColor: "text-white",
  },
  Closed: { label: "Closed", color: "bg-gray-500", textColor: "text-white" },
};

const priorityConfig = {
  Low: { label: "Low", color: "bg-gray-500 text-white" },
  Medium: { label: "Medium", color: "bg-blue-600 text-white" },
  High: { label: "High", color: "bg-orange-500 text-white" },
};

const caseTypeConfig = {
  "Technical Fault Line 1": {
    label: "Technical Fault Line 1",
    color: "bg-red-500",
  },
  "Technical Fault Line 2": {
    label: "Technical Fault Line 2",
    color: "bg-red-400",
  },
  "Base Fault Queue": { label: "Base Fault Queue", color: "bg-orange-600" },
  "Install Problems": { label: "Install Problems", color: "bg-yellow-600" },
  Porting: { label: "Porting", color: "bg-blue-600" },
  "Revisit Required": { label: "Revisit Required", color: "bg-purple-600" },
  "Remote Install Queue": {
    label: "Remote Install Queue",
    color: "bg-indigo-600",
  },
  "Temp Cease of Services": {
    label: "Temp Cease of Services",
    color: "bg-pink-600",
  },
  "Permanent Cease": { label: "Permanent Cease", color: "bg-gray-600" },
  "Office Issue": { label: "Office Issue", color: "bg-green-600" },
  "Billing Query": { label: "Billing Query", color: "bg-teal-600" },
  migrations: { label: "Migrations", color: "bg-teal-600" },
  "Sales Issue": { label: "Sales Issue", color: "bg-cyan-600" },
  "Printer Issue": { label: "Printer Issue", color: "bg-lime-600" },
  "Additional Equipment": {
    label: "Additional Equipment",
    color: "bg-emerald-600",
  },
  "CCTV Fault": { label: "CCTV Fault", color: "bg-amber-600" },
  ESCALATIONS: { label: "ESCALATIONS", color: "bg-rose-600" },
};

// Case Card Component — theme-aware
const CaseCard = ({
  caseItem,
  onClick,
  isDraggable = true,
  isDarkMode = true,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CASE,
    item: { id: caseItem.id, status: caseItem.status, type: caseItem.type },
    canDrag: isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      ref={isDraggable ? drag : null}
      onClick={() => onClick(caseItem)}
      className={`border rounded-lg p-4 mb-3 cursor-pointer transition-all ${
        isDarkMode
          ? "bg-[#0C0E12] border-[#373A41] hover:bg-gray-800 hover:border-gray-600"
          : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
      } ${isDragging ? "opacity-50" : "opacity-100"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${priorityConfig[caseItem.priority].color}`}
        >
          {priorityConfig[caseItem.priority].label}
        </span>
        <span className="text-xs text-gray-400">{caseItem.caseNumber}</span>
      </div>
      <h3
        className={`font-semibold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {caseItem.subject}
      </h3>
      {caseItem.description && (
        <p
          className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {caseItem.description}
        </p>
      )}
      <div className="space-y-1 mb-3">
        {caseItem.account && (
          <div
            className={`flex items-center text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <Building2 className="w-3 h-3 mr-1" />
            <span>{caseItem.account.name}</span>
          </div>
        )}
        {caseItem.contact && (
          <div
            className={`flex items-center text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <User className="w-3 h-3 mr-1" />
            <span>
              {caseItem.contact.firstName} {caseItem.contact.lastName}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-xs mb-2">
        {caseItem.type && (
          <span className={`${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {caseItem.type.charAt(0).toUpperCase() + caseItem.type.slice(1)}
          </span>
        )}
        {caseItem.createdAt && (
          <span className={`${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {formatDate(caseItem.createdAt)}
          </span>
        )}
      </div>
      <div className="mb-2">
        <span
          className={`inline-flex px-2 py-1 rounded text-xs font-medium ${statusConfig[caseItem.status].color} ${statusConfig[caseItem.status].textColor}`}
        >
          {statusConfig[caseItem.status].label}
        </span>
      </div>
      {caseItem.salesforceOwner && (
        <div
          className={`flex items-center gap-2 pt-2 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-xs text-white">
            {caseItem.salesforceOwner.name.charAt(0)}
          </div>
          <span
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {caseItem.salesforceOwner.name}
          </span>
        </div>
      )}
    </div>
  );
};

// Status Column Component
const StatusColumn = ({ status, cases, onDrop, onCaseClick, isDarkMode }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.CASE,
    drop: (item) => onDrop(item.id, status, "status"),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-full sm:w-80 transition-colors ${
        isOver
          ? isDarkMode
            ? "bg-gray-800/50 rounded-lg"
            : "bg-gray-200/50 rounded-lg"
          : ""
      }`}
    >
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm font-medium ${statusConfig[status].color}`}
        >
          {statusConfig[status].label}
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {cases.length}
          </span>
        </span>
      </div>
      <div className="space-y-3">
        {cases.map((caseItem) => (
          <CaseCard
            key={caseItem.id}
            caseItem={caseItem}
            onClick={onCaseClick}
            isDraggable={true}
            isDarkMode={isDarkMode}
          />
        ))}
        {cases.length === 0 && (
          <div
            className={`text-center py-8 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            No cases
          </div>
        )}
      </div>
    </div>
  );
};

// Type Column Component
const TypeColumn = ({ type, cases, onDrop, onCaseClick, isDarkMode }) => {
  const typeLabel = type || "No Type";
  const typeColor = type
    ? caseTypeConfig[type]?.color || "bg-gray-600"
    : "bg-gray-600";

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.CASE,
    drop: (item) => onDrop(item.id, type, "type"),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-full sm:w-80 transition-colors ${
        isOver
          ? isDarkMode
            ? "bg-gray-800/50 rounded-lg"
            : "bg-gray-200/50 rounded-lg"
          : ""
      }`}
    >
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm font-medium ${typeColor}`}
        >
          {typeLabel}
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {cases.length}
          </span>
        </span>
      </div>
      <div className="space-y-3">
        {cases.map((caseItem) => (
          <CaseCard
            key={caseItem.id}
            caseItem={caseItem}
            onClick={onCaseClick}
            isDraggable={true}
            isDarkMode={isDarkMode}
          />
        ))}
        {cases.length === 0 && (
          <div
            className={`text-center py-8 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            No cases
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Contact Timer Helpers ────────────────────────────────────────────────────
const CONTACT_DEADLINE_MS = 4 * 60 * 60 * 1000;

const getContactTimerInfo = (caseItem, now) => {
  if (caseItem.contactedAt) return { done: true };
  const start = new Date(caseItem.contactTimerStartedAt || caseItem.createdAt);
  const elapsed = now - start.getTime();
  const remaining = CONTACT_DEADLINE_MS - elapsed;
  return { done: false, remaining, overdue: remaining <= 0 };
};

const formatCountdown = (ms) => {
  if (ms <= 0) return "Overdue";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
};

const formatElapsedShort = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const CountdownBadge = ({ caseItem, now, isDarkMode = false }) => {
  const info = getContactTimerInfo(caseItem, now);
  if (info.done) {
    const elapsed = formatElapsedShort(caseItem.contactTimeElapsedSeconds);
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${isDarkMode ? "bg-green-900 text-green-300" : "bg-green-100 text-green-700"}`}
      >
        <Clock className="w-3 h-3" />
        Contacted{elapsed ? ` (${elapsed})` : ""}
      </span>
    );
  }
  const urgent = !info.overdue && info.remaining <= 60 * 60 * 1000;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
        info.overdue
          ? isDarkMode
            ? "bg-red-700 text-red-100"
            : "bg-red-600 text-white"
          : urgent
            ? isDarkMode
              ? "bg-red-900 text-red-300"
              : "bg-red-100 text-red-700"
            : isDarkMode
              ? "bg-orange-900 text-orange-300"
              : "bg-orange-100 text-orange-700"
      }`}
    >
      <Clock className="w-3 h-3" />
      {formatCountdown(info.remaining)}
    </span>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Main Cases Component
const Cases = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    type: "",
  });

  const handleRealtimeUpdate = useCallback((data) => {
    if (data.type === "case_updated") {
      const { action, data: caseData } = data;
      setCases((prevCases) => {
        if (action === "created") return [caseData, ...prevCases];
        else if (
          action === "updated" ||
          action === "status_changed" ||
          action === "assigned"
        )
          return prevCases.map((c) =>
            c.id === caseData.id ? { ...c, ...caseData } : c,
          );
        else if (action === "deleted")
          return prevCases.filter((c) => c.id !== caseData.id);
        return prevCases;
      });
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["case_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  const [timerNow, setTimerNow] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setTimerNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    fetchCases();
  }, [filters, viewMode]);

  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [supportTeamId, setSupportTeamId] = useState(null);

  useEffect(() => {
    const fetchSupportTeam = async () => {
      try {
        const teamsRes = await apiService.getTeams();
        const teams = teamsRes.data || [];
        const supportTeam = teams.find(
          (t) => t.type === "support" || t.type === "operations",
        );
        if (supportTeam) setSupportTeamId(supportTeam.id);
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchSupportTeam();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCases(filters);
      setCases(response.data || []);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCase = async (id, updates) => {
    try {
      await apiService.updateCase(id, updates);
      fetchCases();
    } catch (error) {
      console.error("Error updating case:", error);
      alert(error.response?.data?.message || "Failed to update case");
    }
  };

  const handleDrop = async (caseId, newValue, dropType) => {
    if (dropType === "status")
      await handleUpdateCase(caseId, { status: newValue });
    else if (dropType === "type")
      await handleUpdateCase(caseId, { type: newValue });
  };

  const handleCaseClick = (caseItem) => navigate(`/cases/${caseItem.id}`);

  const getFilteredCases = () => {
    let filtered = cases;
    if (viewMode === "kanban-status" || viewMode === "kanban-type") {
      filtered = filtered.filter((c) => c.status !== "Closed");
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (caseItem) =>
          caseItem.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          caseItem.caseNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          caseItem.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }
    return filtered;
  };

  const filteredCases = getFilteredCases();
  const getCasesByStatus = (status) =>
    filteredCases.filter((c) => c.status === status);
  const getCasesByType = (type) =>
    type === null
      ? filteredCases.filter((c) => !c.type)
      : filteredCases.filter((c) => c.type === type);

  const getUniqueTypes = () => {
    const types = new Set();
    filteredCases.forEach((c) =>
      c.type ? types.add(c.type) : types.add(null),
    );
    return Array.from(types).sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    });
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
        }`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-700"}>
          Loading...
        </div>
      </div>
    );
  }

  // Shared select class
  const selectCls = isDarkMode
    ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700"
    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50";

  const viewBtnInactive = isDarkMode
    ? "bg-[#0C0E12] border border-gray-700 text-gray-400 hover:bg-gray-700"
    : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-50";

  return (
    <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
      <div
        className={`min-h-screen font-inter ${
          isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"
        }`}
      >
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <h1
                className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Cases
              </h1>
              <p
                className={`text-sm sm:text-base leading-relaxed ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Manage customer support cases
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
              <button
                onClick={() => setShowAutoAssignModal(true)}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  border: "1px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `0px 36px 32px -16px #427BCA36, 0px 8px 16px 0px #2F578F1F, 0px 2px 4px -8px #427BCA14, 0px 8px 24px -16px #FFFFFF3D inset, 0px -24px 32px 0px #FFFFFF38 inset, 0px 0px 12px 0px #FFFFFF inset`,
                  borderImageSlice: "1",
                }}
              >
                <Users size={16} className="shrink-0" />
                <span className="whitespace-nowrap">Auto-Assign</span>
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  border: "1px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `0px 36px 32px -16px #427BCA36, 0px 8px 16px 0px #2F578F1F, 0px 2px 4px -8px #427BCA14, 0px 8px 24px -16px #FFFFFF3D inset, 0px -24px 32px 0px #FFFFFF38 inset, 0px 0px 12px 0px #FFFFFF inset`,
                  borderImageSlice: "1",
                }}
              >
                <Add size={16} className="shrink-0" />
                <span className="whitespace-nowrap">New Case</span>
              </button>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex-1 relative max-w-md">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  isDarkMode ? "text-gray-400" : "text-gray-400"
                }`}
              />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {viewMode === "list" && (
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  className={`px-3 py-2 border rounded-lg text-sm transition-colors ${selectCls}`}
                >
                  <option value="">All Statuses</option>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={filters.priority}
                onChange={(e) =>
                  setFilters({ ...filters, priority: e.target.value })
                }
                className={`px-3 py-2 border rounded-lg text-sm transition-colors ${selectCls}`}
              >
                <option value="">All Priorities</option>
                {Object.entries(priorityConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>

              {viewMode === "list" && (
                <select
                  value={filters.type}
                  onChange={(e) =>
                    setFilters({ ...filters, type: e.target.value })
                  }
                  className={`px-3 py-2 border rounded-lg text-sm transition-colors ${selectCls}`}
                >
                  <option value="">All Types</option>
                  {Object.entries(caseTypeConfig).map(([key, config]) => {
                    const count = cases.filter((c) => c.type === key).length;
                    return (
                      <option key={key} value={key}>
                        {config.label} ({count})
                      </option>
                    );
                  })}
                </select>
              )}

              <div className="flex gap-2">
                {[
                  {
                    mode: "kanban-status",
                    Icon: LayoutGrid,
                    title: "Kanban by Status",
                  },
                  {
                    mode: "kanban-type",
                    Icon: Layers,
                    title: "Kanban by Type (Open Cases)",
                  },
                  { mode: "list", Icon: LayoutList, title: "List View" },
                ].map(({ mode, Icon, title }) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === mode
                        ? "bg-blue-600 text-white"
                        : viewBtnInactive
                    }`}
                    title={title}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Kanban Board by Status */}
          {viewMode === "kanban-status" && (
            <div
              className={`px-4 sm:px-6 lg:px-8 py-6 rounded-lg ${
                isDarkMode ? "bg-[#13161B]" : "bg-gray-200/60"
              }`}
            >
              <div className="flex flex-col sm:flex-row gap-6 overflow-x-auto pb-4">
                {Object.keys(statusConfig)
                  .filter((status) => status !== "Closed")
                  .map((status) => (
                    <StatusColumn
                      key={status}
                      status={status}
                      cases={getCasesByStatus(status)}
                      onDrop={handleDrop}
                      onCaseClick={handleCaseClick}
                      isDarkMode={isDarkMode}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Kanban Board by Type */}
          {viewMode === "kanban-type" && (
            <div
              className={`px-4 sm:px-6 lg:px-8 py-6 rounded-lg ${
                isDarkMode ? "bg-[#13161B]" : "bg-gray-200/60"
              }`}
            >
              <div className="mb-4">
                <h2
                  className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Open Cases by Type
                </h2>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Viewing {filteredCases.length} open case
                  {filteredCases.length !== 1 ? "s" : ""} (Closed cases
                  excluded)
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-6 overflow-x-auto pb-4">
                {getUniqueTypes().map((type) => (
                  <TypeColumn
                    key={type || "no-type"}
                    type={type}
                    cases={getCasesByType(type)}
                    onDrop={handleDrop}
                    onCaseClick={handleCaseClick}
                    isDarkMode={isDarkMode}
                  />
                ))}
                {getUniqueTypes().length === 0 && (
                  <div
                    className={`text-center py-12 w-full ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    No open cases found
                  </div>
                )}
              </div>
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <div
              className={`rounded-lg border overflow-hidden ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead
                    className={`border-b ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <tr>
                      <th
                        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                        style={{ maxWidth: "380px" }}
                      >
                        Subject
                      </th>
                      {[
                        "Account",
                        "Status",
                        "Priority",
                        "Type",
                        "Owner",
                        "Days Open",
                        "SLA Timer",
                      ].map((h) => (
                        <th
                          key={h}
                          className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${
                      isDarkMode ? "divide-gray-700" : "divide-gray-200"
                    }`}
                  >
                    {cases
                      .filter(
                        (caseItem) =>
                          caseItem.subject
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          caseItem.caseNumber
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          caseItem.description
                            ?.toLowerCase()
                            .includes(searchTerm.toLowerCase()),
                      )
                      .map((caseItem) => (
                        <tr
                          key={caseItem.id}
                          onClick={() => handleCaseClick(caseItem)}
                          className={`cursor-pointer transition-colors ${(() => {
                            const info = getContactTimerInfo(
                              caseItem,
                              timerNow,
                            );
                            if (info.done)
                              return isDarkMode
                                ? "hover:bg-gray-800"
                                : "hover:bg-gray-50";
                            if (
                              info.overdue ||
                              info.remaining <= 60 * 60 * 1000
                            )
                              return isDarkMode
                                ? "bg-red-950/40 hover:bg-red-950/60 border-l-2 border-red-600"
                                : "bg-red-50 hover:bg-red-100 border-l-2 border-red-500";
                            return isDarkMode
                              ? "hover:bg-gray-800"
                              : "hover:bg-gray-50";
                          })()}`}
                        >
                          <td
                            className="px-4 py-3"
                            style={{ maxWidth: "380px" }}
                          >
                            <div
                              className={`font-medium truncate ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {caseItem.subject}
                            </div>
                            {caseItem.description && (
                              <div
                                className={`text-sm line-clamp-1 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                {caseItem.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div
                              className={`text-sm ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {caseItem.account?.name || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig[caseItem.status].color} ${statusConfig[caseItem.status].textColor}`}
                            >
                              {statusConfig[caseItem.status].label}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${priorityConfig[caseItem.priority].color}`}
                            >
                              {priorityConfig[caseItem.priority].label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {caseItem.type
                                ? caseItem.type.charAt(0).toUpperCase() +
                                  caseItem.type.slice(1)
                                : "—"}
                            </span>
                          </td>
                          <td
                            className={`px-4 py-3 text-sm ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {caseItem.owner?.name ||
                              caseItem.salesforceOwner?.name ||
                              "Unassigned"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              const created = new Date(
                                caseItem.salesforceCreatedDate ||
                                  caseItem.createdAt,
                              );
                              const days = Math.floor(
                                (new Date() - created) / (1000 * 60 * 60 * 24),
                              );
                              return (
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    days >= 14
                                      ? isDarkMode
                                        ? "bg-red-900 text-red-300"
                                        : "bg-red-100 text-red-700"
                                      : days >= 7
                                        ? isDarkMode
                                          ? "bg-orange-900 text-orange-300"
                                          : "bg-orange-100 text-orange-700"
                                        : isDarkMode
                                          ? "bg-gray-700 text-gray-300"
                                          : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  {days}d
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <CountdownBadge
                              caseItem={caseItem}
                              now={timerNow}
                              isDarkMode={isDarkMode}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {cases.filter(
                (caseItem) =>
                  caseItem.subject
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  caseItem.caseNumber
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  caseItem.description
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()),
              ).length === 0 && (
                <div
                  className={`text-center py-12 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  No cases found
                </div>
              )}
            </div>
          )}
        </div>

        {showCreateModal && (
          <CreateCaseModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchCases();
            }}
          />
        )}
        {showAutoAssignModal && (
          <AutoAssignCasesModal
            isOpen={showAutoAssignModal}
            onClose={() => setShowAutoAssignModal(false)}
            onSuccess={() => {
              setShowAutoAssignModal(false);
              fetchCases();
            }}
            teamId={supportTeamId}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default Cases;
